import type { ReactElement, ReactNode } from 'react';
import type { TopicRoomRow } from '../../api';
import { Link } from '../../components/Link';
import { PageBack } from '../../components/PageBack';
import './topic.css';

/**
 * What the two topic-room pages share (2026-10-26 founder, rooms by topic): the bar, the seat a person will get once
 * they are in (its room nickname and picture are handed out by the server when they come in, so the page draws a
 * place for them, not a guess), and the "3 of 3" state.
 */

/** How long the line on a seat can be (edge config.js ROOM.INTRO_MAX_CHARS). */
export const INTRO_MAX_CHARS = 300;
/** How long a topic can be (edge config.js ROOM.TOPIC_MAX_CHARS). */
export const TOPIC_MAX_CHARS = 60;
/** Rooms one person holds at once, topics up and rooms sat in together (edge config.js ROOM.SEATS_PER_PERSON). */
export const ROOMS_CAP = 3;
/** A topic room opens with this many in (edge config.js ROOM.TOPIC_MIN_MEMBERS). */
export const OPENS_AT = 3;

/** A room's count: who is waiting in one still forming, who is inside an open one and how much space is left. */
export const countLine = (r: Pick<TopicRoomRow, 'status' | 'present' | 'space'>): string => (r.status === 'forming'
  ? `${r.present} waiting · opens when ${OPENS_AT} are in`
  : `${r.present} inside · room for ${r.space} more`);

/** The page's bar: Back on the left, a step or a label in the middle. */
export function TopicBar({ to, onIntercept, children }: { to: string; onIntercept?: () => boolean; children?: ReactNode }): ReactElement {
  return (
    <header className="topic-bar">
      <PageBack to={to} className="topic-back" onIntercept={onIntercept} />
      <div className="topic-bar-mid">{children}</div>
      <span className="topic-bar-end" aria-hidden />
    </header>
  );
}

/**
 * A seat no one has yet — the third one a waiting room keeps, or theirs while it is being got ready: an empty dashed place
 * (2026-10-26 founder: a star in it, where their own seat stood, read as nothing; their seat is shown as it will be).
 */
export function SeatToCome({ size }: { size: number }): ReactElement {
  return <span className="topic-seat-tocome" style={{ width: size, height: size }} aria-hidden />;
}

/** A seat's name colour, by its place in the list (the thread's own: chat-skin --chat-n1..3). */
export const seatInk = (i: number): string => `topic-ink-${(i % 3) + 1}`;

export const CHEVRON = (
  <svg className="topic-chev" viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const PENCIL = (
  <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
    <path d="M4 20h4L19 9l-4-4L4 16v4z" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinejoin="round" />
  </svg>
);

export const FLAME = (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
    <path d="M12 2.5c1.2 3.2 5.5 5.6 5.5 10.6A5.5 5.5 0 0 1 6.5 13c0-2.3 1.1-3.7 2.3-5 .3 1.6 1.1 2.7 2.2 3.2C10.6 8.6 10.9 5.2 12 2.5z" fill="#F4720C" />
    <path d="M12 12.2c.8 1.4 2.4 2.3 2.4 4.2a2.4 2.4 0 0 1-4.8 0c0-1.5 1-2.6 2.4-4.2z" fill="#FFB010" />
  </svg>
);

/**
 * Holding as many rooms as one may (AlreadyThree): topics up and rooms sat in count together; to make space they stop
 * a topic or leave a room, on the New friends page.
 */
export function RoomsFull({ topic }: { topic?: string }): ReactElement {
  const cap = ROOMS_CAP;
  return (
    <div className="topic-state" data-log-seen="topic_rooms_full">
      <span className="topic-full-dots" aria-hidden><i /><i /><i /></span>
      <h1 className="topic-h1">You’ve got {cap} going already</h1>
      <p className="topic-state-body">That’s the most at once! To make space{topic ? ` for ${topic}` : ''}, stop a topic or leave a room. It’s all here.</p>
      <Link to="/friends" className="topic-rooms-card" data-log="topic_rooms_full_open">
        <span className="topic-rooms-card-dots" aria-hidden><i /><i /><i /></span>
        <span className="topic-rooms-card-text">
          <span className="topic-rooms-card-title">My rooms</span>
          <span className="topic-rooms-card-sub">Rooms · {cap} of {cap}</span>
        </span>
        {CHEVRON}
      </Link>
    </div>
  );
}
